import { Trans, useLingui } from '@lingui/react/macro';

import { PaginationFooter, type PaginationProps } from '@/design_system/Pagination/Pagination';
import { Body, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { ShipmentWithRelations } from '@/models/shipment';

export const SHIPMENTS_PER_PAGE = 20;

import { SortableColumnHeader } from '@/design_system/Table/SortableColumnHeader';
import { useFilters } from '@/routes/Shipments/Filters/FiltersState';
import { ShipmentReferenceAndCarrierCell } from '@/routes/Shipments/Tabs/components/ShipmentsTable/components/ShipmentReferenceAndCarrierCell';

import { ArticlePhotoGroupCell } from './components/ArticlePhotoGroupCell';
import { ShipmentDestinationCell } from './components/ShipmentDestinationCell';
import { ShipmentOriginCell } from './components/ShipmentOriginCell';
import { ShipmentStepCell } from './components/ShipmentStepCell';
import { ShipmentStepDueDateCell } from './components/ShipmentStepDueDateCell';

export const ShipmentsTable = ({
  shipments,
  showStepDueDate,
  pagination,
  isLoading,
}: {
  shipments: ShipmentWithRelations[];
  showStepDueDate?: boolean;
  pagination?: PaginationProps;
  isLoading?: boolean;
}) => {
  const { t } = useLingui();

  const { sort } = useFilters();

  const handleChangeSort = (columnValue: 'reference' | 'statusDueAt') => {
    sort?.setSort((prev) => {
      return {
        field:
          prev.field !== columnValue
            ? columnValue
            : prev.order === 'asc'
              ? columnValue
              : 'statusDueAt',
        order: prev.field !== columnValue || prev.order === 'desc' ? 'asc' : 'desc',
      };
    });
  };

  const columnWidths = Object.values({
    reference: '104px',
    items: 'minmax(auto, 128px)',
    step: 'minmax(120px, 1fr)',
    stepDueDate: showStepDueDate && 'minmax(120px, 1fr)',
    origin: 'minmax(120px, 1fr)',
    destination: 'minmax(120px, 1fr)',
  });

  return (
    <Table
      ariaLabel={t({ id: 'shipments.table.label', message: 'Shipments' })}
      columnWidths={columnWidths}
    >
      <Header>
        <Row>
          <SortableColumnHeader
            isActive={sort?.sort.field === 'reference'}
            onChange={() => handleChangeSort('reference')}
            sortOrder={sort?.sort.order}
          >
            <Trans id="shipments.table.column.reference.title">Reference</Trans>
          </SortableColumnHeader>

          <Column>
            <Trans id="shipments.table.column.articles.title">Items</Trans>
          </Column>

          <Column>
            <Trans id="shipments.table.column.step.title">Step</Trans>
          </Column>

          {showStepDueDate && (
            <SortableColumnHeader
              isActive={sort?.sort.field === 'statusDueAt'}
              onChange={() => handleChangeSort('statusDueAt')}
              sortOrder={sort?.sort.order}
            >
              <Trans id="shipments.table.column.step-due-date.title">Step due date</Trans>
            </SortableColumnHeader>
          )}

          <Column>
            <Trans id="shipments.table.column.origin.title">Origin</Trans>
          </Column>

          <Column>
            <Trans id="shipments.table.column.destination.title">Destination</Trans>
          </Column>
        </Row>
      </Header>

      {!isLoading && (
        <Body>
          {shipments?.map((shipment) => (
            <ShipmentsTableRow
              key={shipment.id}
              shipment={shipment}
              showStepDueDate={showStepDueDate}
            />
          ))}
        </Body>
      )}

      {pagination && shipments?.length > 0 && !isLoading && <PaginationFooter {...pagination} />}
    </Table>
  );
};

const ShipmentsTableRow = ({
  shipment,
  showStepDueDate,
}: {
  shipment: ShipmentWithRelations;
  showStepDueDate?: boolean;
}) => {
  return (
    <Row to={`/shipments/${shipment.id}`}>
      <ShipmentReferenceAndCarrierCell shipment={shipment} />

      <ArticlePhotoGroupCell shipment={shipment} />

      <ShipmentStepCell shipment={shipment} />

      {showStepDueDate && <ShipmentStepDueDateCell shipment={shipment} />}

      <ShipmentOriginCell shipment={shipment} />

      <ShipmentDestinationCell shipment={shipment} />
    </Row>
  );
};
